"use client";

import type React from "react";
import { useEffect, useRef } from "react";
import {
    useCountry,
    useInitialConfig,
    useIsWebview,
    usePageParams,
} from "../../../../components/ApplicationState/hooks";
import { useConsent } from "../../../../components/Consent/hooks";
import { pushDataLayer } from "../../../../helpers/dataLayer";
import type { DataLayerItemPageTrack } from "../../../../helpers/types";
import { useIsPageChange } from "../../../../hooks/useIsPageChange";
import { useLocation } from "../../../../network/matlocq/queries/Location";

type Props = {
    children: React.ReactNode;
};

const Template: React.FC<Props> = ({ children }) => {
    const isPageTracked = useRef(false);
    const consent = useConsent();
    const country = useCountry();
    const isWebview = useIsWebview();
    const webviewInitialConfig = useInitialConfig();
    const isPageChange = useIsPageChange();
    const mobileWebType =
        isWebview !== null && isWebview
            ? (webviewInitialConfig?.mobileWebType ?? "web-app-react/unknown")
            : "web-app-react";
    const pageParams = usePageParams();
    const { data: location, isLoading: isLoadingLocation } = useLocation(
        pageParams.code ?? null
    );

    useEffect(() => {
        if (isPageChange) {
            isPageTracked.current = false;
            return;
        }

        if (!isPageTracked.current && consent && !isLoadingLocation) {
            const dataLayerItem: DataLayerItemPageTrack = {
                type: "pageTrack",
                event: "pagetrack",
                mobileWebType,
                pageName: location?.name ?? "main",
                pageType: "forecast",
                pageTypeDetail: "today",
                pageCountry: country,
                tcontent: "main",
                tcdist: "main",
                pageRegion: location?.admin1.location.name ?? "main",
                pageCity: location?.name ?? "main",
                pageId: location?.code ?? "main",
                overlay: undefined,
                overlayType: undefined,
            };

            pushDataLayer(dataLayerItem);
            isPageTracked.current = true;
        }
    }, [
        country,
        consent,
        mobileWebType,
        isLoadingLocation,
        location,
        pageParams,
        isPageChange,
    ]);

    return children;
};

export default Template;
